import { IconeRegistros } from './icones'
import { useEffect, useMemo, useState } from 'react'
import { FolhaInferior } from './workspace/FolhaInferior'

export type CategoriaRegistro =
  | 'fase'
  | 'retencao'
  | 'srs'
  | 'impacto'
  | 'aprisionamento'
  | 'ejecao'
  | 'fim'
  | 'decisao'

export interface RegistroSimulacao {
  id: string
  tempo: number
  titulo: string
  descricao: string
  categoria: CategoriaRegistro
}

interface Props {
  registros: readonly RegistroSimulacao[]
  tempo: number
  sessao: string
  aoIrPara: (tempo: number) => void
}

/**
 * Console didático da simulação.
 *
 * A lista cresce conforme a reprodução avança. O maior instante alcançado fica
 * guardado durante a sessão para que voltar a um registro antigo não apague os
 * eventos posteriores. Clicar em um item pausa e posiciona a timeline.
 *
 * O painel usa a `FolhaInferior` do workspace. Ele tinha a própria implementação
 * de folha, feita só com media query, sem `role="dialog"`, sem Escape e sem
 * devolver o foco — três coisas que a primitiva compartilhada resolve de uma vez
 * para ele e para as ferramentas do celular.
 */
export function CentralLogs({ registros, tempo, sessao, aoIrPara }: Props) {
  const [aberta, setAberta] = useState(false)
  const [maiorTempo, setMaiorTempo] = useState(tempo)
  const [selecionado, setSelecionado] = useState<string | null>(null)

  useEffect(() => {
    setMaiorTempo(tempo)
    setSelecionado(null)
    setAberta(false)
  }, [sessao])

  useEffect(() => {
    setMaiorTempo((atual) => Math.max(atual, tempo))
  }, [tempo])

  const visiveis = useMemo(
    () => registros.filter((registro) => registro.tempo <= maiorTempo + 0.001),
    [registros, maiorTempo],
  )
  const registroSelecionado = registros.find((registro) => registro.id === selecionado) ?? null

  const selecionar = (registro: RegistroSimulacao) => {
    setSelecionado(registro.id)
    aoIrPara(registro.tempo)
    setAberta(false)
  }

  return (
    <>
      <button
        className="botao-central-logs"
        aria-expanded={aberta}
        onClick={() => setAberta((valor) => !valor)}
      >
        <IconeRegistros tamanho={20} />
        <span>
          {registroSelecionado
            ? `${registroSelecionado.titulo} · ${Math.round(registroSelecionado.tempo * 100)}%`
            : `Registros · ${visiveis.length}`}
        </span>
      </button>

      <FolhaInferior
        aberta={aberta}
        titulo="Central de registros"
        subtitulo="Toque em um evento para pausar e voltar ao instante correspondente."
        aoFechar={() => setAberta(false)}
        fracaoAltura={0.62}
        className="central-logs"
      >
        <ol className="lista-logs">
          {visiveis.length === 0 && (
            <li className="logs-vazios">
              Reproduza a simulação para registrar os acontecimentos.
            </li>
          )}
          {visiveis.map((registro) => (
            <li key={registro.id}>
              <button
                className={selecionado === registro.id ? 'registro-log selecionado' : 'registro-log'}
                onClick={() => selecionar(registro)}
              >
                <time>{Math.round(registro.tempo * 100)}%</time>
                <span className="marcador-log" aria-hidden="true" />
                <span className="conteudo-log">
                  <strong>{registro.titulo}</strong>
                  <small>{registro.descricao}</small>
                </span>
                <span className="categoria-log">{registro.categoria}</span>
              </button>
            </li>
          ))}
        </ol>
      </FolhaInferior>

    </>
  )
}
